"use client";

import React from "react";
import { cn } from "utils/utils";
import { FinishEnum } from "modules/cards/model/card";
import { useSets } from "modules/common/hooks/useSets";
import ConditionChip from "modules/common/components/Table/ConditionChip";
import LanguageFlag from "modules/common/components/Table/LanguageFlag";
import FoilIndicator from "modules/common/components/Table/FoilIndicator";
import {
  SetCodeMark,
  SetIcon,
} from "modules/common/components/SetIcon";
import { useCardGridItemContext } from "./CardGridItemContext";
import { CardGridItemAnyPrintingBadge } from "./CardGridItemAnyPrintingBadge";
import {
  hasFoilVisual,
  primaryDisplayFinish,
} from "modules/cards/model/finishBehavior";
import { CardFinishIndicator } from "modules/common/components/CardSpecOverlay";

interface CardGridItemDetailsProps {
  /** Show minimum condition indicator (≥) for wants lists. Default: false */
  showMinimum?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export function CardGridItemDetails({
  showMinimum = false,
  className,
  children,
}: CardGridItemDetailsProps) {
  const { item } = useCardGridItemContext();
  const { getSetName } = useSets();
  const setName = item.game
    ? getSetName(item.setCode, item.game)
    : item.setCode;

  const isFoil = hasFoilVisual(item.finishes);
  const finish = primaryDisplayFinish(item.finishes);

  return (
    <div className={cn("flex items-center justify-between", className)}>
      <div className="flex items-center gap-0.5 md:gap-1">
        <div className="contents md:hidden">
          {item.anyPrinting ? (
            <CardGridItemAnyPrintingBadge
              className="sm:inline-flex"
              iconSize={20}
            />
          ) : (
            <div className="flex items-center">
              {item.game ? (
                <SetIcon
                  setCode={item.setCode}
                  game={item.game}
                  size="xl"
                  className={cn(
                    "text-secondary",
                    isFoil && "ss-foil ss-grad"
                  )}
                />
              ) : (
                <SetCodeMark
                  setCode={item.setCode}
                  size="xl"
                  className="text-secondary"
                />
              )}
            </div>
          )}
          <ConditionChip
            condition={item.condition}
            showMinimum={showMinimum}
            size="sm"
          />
          <LanguageFlag language={item.language} size="sm" />
        </div>
        <div className="hidden md:contents">
          {item.anyPrinting ? (
            <CardGridItemAnyPrintingBadge
              className="sm:inline-flex"
              iconSize={32}
            />
          ) : (
            <div
              className="lg:tooltip lg:tooltip-secondary flex items-center"
              data-tip={setName || item.setCode}
            >
              {item.game ? (
                <SetIcon
                  setCode={item.setCode}
                  game={item.game}
                  className="text-secondary"
                />
              ) : (
                <SetCodeMark
                  setCode={item.setCode}
                  className="text-secondary"
                />
              )}
            </div>
          )}
          <ConditionChip condition={item.condition} showMinimum={showMinimum} />
          <LanguageFlag language={item.language} />
        </div>
        {finish && (
          <span className="hidden md:inline-flex">
            <CardFinishIndicator finish={finish} />
          </span>
        )}
      </div>

      {children && <div className="flex items-center">{children}</div>}
    </div>
  );
}
